<template>
  <section class="page-callout module">
    <div
      ref="trigger"
      class="container">
      <div
        v-if="hasContent()"
        class="page-callout-content">
        <div
          class="headline h3"
          v-html="data.page_callout_headline"></div>
        <div
          class="text font-larger editor-formatting"
          v-html="data.page_callout_text"></div>
        <a
          v-if="data.page_callout_link"
          :href="data.page_callout_link.url"
          :target="data.page_callout_link.target"
          class="btn"
          v-html="data.page_callout_link.title">
        </a>
      </div>
      <div
        class="page-callout-hex-container"
        :class="{ 'no-content': !hasContent() }">
        <div
          ref="outlineOne"
          class="hex-outline one"></div>
        <div
          ref="hexText"
          class="hex text h4"
          :class="{ 'has-image': data.page_callout_img_3 }">
          <div class="hex-wrapper">
            <Imager
              v-if="data.page_callout_img_3"
              :image="data.page_callout_img_3"
              :native="true" />
            <div
              class="text"
              v-html="data.page_callout_hex_text"></div>
          </div>
        </div>
        <div
          ref="hexOne"
          class="hex one"
          :class="{ 'has-image': data.page_callout_img_1 }">
          <div class="hex-wrapper">
            <Imager
              :image="data.page_callout_img_1"
              :native="true" />
          </div>
        </div>
        <div
          ref="outlineTwo"
          class="hex-outline two"></div>
        <div
          ref="hexTwo"
          class="hex two"
          :class="{ 'has-image': data.page_callout_img_2 }">
          <div class="hex-wrapper">
            <Imager
              :image="data.page_callout_img_2"
              :native="true" />
          </div>
        </div>
        <div
          ref="outlineTwo"
          class="hex-outline three"></div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "PageCalloutComponent",
    "CLASSNAME": "page-callout",
    "URL": "http://insitro.local/test/"
   }
  #ENDMETA */
import Imager from '@/components/utility/Imager.vue'
import { gsap, Linear } from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { ref, onMounted, onBeforeUnmount } from 'vue'

gsap.registerPlugin(ScrollTrigger)

const props = defineProps(['data'])

const trigger = ref(null)
const hexOne = ref(null)
const hexTwo = ref(null)
const hexText = ref(null)
const outlineOne = ref(null)
const outlineTwo = ref(null)

let tl

const hasContent = () => {
  if (
    props.data.page_callout_headline ||
    props.data.page_callout_text ||
    props.data.page_callout_link
  ) {
    return true
  } else {
    return false
  }
}

onMounted(() => {
  tl = gsap.timeline({
    scrollTrigger: {
      trigger: trigger.value,
      start: 'top bottom',
      end: 'bottom top',
      scrub: true,
    },
  })

  tl.to(
    hexOne.value,
    {
      y: '-20%',
      ease: Linear.easeNone,
    },
    0
  )
    .to(
      hexText.value,
      {
        y: '5%',
        ease: Linear.easeNone,
      },
      0
    )
    .to(
      hexTwo.value,
      {
        y: '-10%',
        ease: Linear.easeNone,
      },
      0
    )
    .to(
      outlineOne.value,
      {
        autoAlpha: 1,
        ease: Linear.easeNone,
      },
      0
    )
    .to(
      outlineTwo.value,
      {
        autoAlpha: 0,
        ease: Linear.easeNone,
      },
      0
    )
})

onBeforeUnmount(() => {
  tl.kill()
})
</script>

<style lang="scss">
.page-callout {
  .container {
    display: flex;
    flex-direction: column;

    @include tablet-landscape(1) {
      flex-direction: column;
    }
  }

  .page-callout-content {
    position: absolute;
    top: 20px;
    left: 0;
    max-width: 420px;

    @include tablet-landscape(1) {
      position: relative;
      top: 0;
      width: 100%;
      max-width: 428px;
    }

    .text {
      margin: 20px 0 0;
    }

    .btn {
      margin: 30px 0 0;
    }
  }

  .page-callout-hex-container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-self: flex-end;
    width: 62%;
    max-width: 800px;
    padding: 0 0 70px;

    &.no-content {
      align-self: center;
    }

    @include tablet-landscape(1) {
      align-self: center;
      width: 100%;
    }

    .hex {
      position: relative;
      width: 52%;
      height: 0;
      padding-bottom: 59%;
      clip-path: polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%);
      color: $blue;
      background-color: $tech-blue;

      @include tablet-landscape(1) {
        width: 62%;
        padding-bottom: 69%;
      }

      &.has-image {
        background-color: transparent;
      }

      &.text {
        align-self: center;
        transform: translateX(65px);

        @include mobile {
          font-size: 6vw;
        }
      }

      &.one {
        align-self: flex-start;
        width: 48%;
        padding-bottom: 55%;
        margin-top: -25%;

        @include tablet-landscape(1) {
          width: 58%;
          padding-bottom: 65%;
          margin-top: -14%;
        }
      }

      &.two {
        align-self: flex-end;
        width: 40%;
        padding-bottom: 46%;
        margin-top: -34%;

        @include tablet-landscape(1) {
          width: 50%;
          padding-bottom: 56%;
        }
      }

      .hex-wrapper {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
      }

      .text {
        width: 80%;
        max-width: 347px;
        margin: auto;
        z-index: 1;
      }

      .imager {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        img {
          width: 100% !important;
          height: 100%;
          object-fit: cover;
        }
      }
    }

    .hex-outline {
      position: absolute;
      background-image: url('@/assets/img/page-callout-hex.svg');
      background-size: contain;
      background-repeat: no-repeat;

      &.one {
        opacity: 0;
        visibility: hidden;
        right: 0;
        top: 40%;
        width: 97px;
        height: 111px;
        transform: translateY(-50%) rotate(90deg);
      }

      &.two {
        width: 158px;
        height: 181px;
        left: 0;
        bottom: 140px;
      }

      &.three {
        bottom: 0;
        left: 50%;
        width: 221px;
        height: 253px;
        transform: translateX(-50%) rotate(-60deg);

        @include mobile {
          width: 158px;
          height: 181px;
        }
      }
    }
  }
}
</style>
